import type { SessionResponse } from '@comficonf/shared';
import { QueryClient } from '@tanstack/react-query';
import type { ApiError } from './client';
import { createApiClient, isApiError } from './client';
import { createEndpoints } from './endpoints';
import { queryKeys } from './keys';

let unauthenticatedHandler: ((error: ApiError) => void) | null = null;

/** Registers what happens when any request reports 401 UNAUTHENTICATED (the app redirects to /login). */
export function setUnauthenticatedHandler(handler: ((error: ApiError) => void) | null): void {
  unauthenticatedHandler = handler;
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  if (!isApiError(error)) return true;
  if (error.code === 'NETWORK_ERROR') return true;
  if (error.status < 500) return false;
  return !['SERVER_OFFLINE', 'SERVER_DISCONNECTED', 'INVALID_RESPONSE'].includes(error.code);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 15_000,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

export const queryClient = createQueryClient();

export const apiClient = createApiClient({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  getCsrfToken: async () => {
    const cached = queryClient.getQueryData<SessionResponse>(queryKeys.session);
    if (cached) return cached.csrfToken;
    const session = await queryClient.fetchQuery({
      queryKey: queryKeys.session,
      queryFn: ({ signal }) => api.getSession(signal),
      staleTime: Infinity,
    });
    return session.csrfToken;
  },
  onUnauthenticated: (error) => unauthenticatedHandler?.(error),
});

export const api = createEndpoints(apiClient);
